import type { ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import type { Equipment } from "@/hooks/useEquipment";
import { formatCurrency } from "@/lib/utils";
import { formatDate } from "@/lib/date-utils";
import type { SelectableColumn } from "@/hooks/useColumnSelection";

export interface EquipmentListCellContext {
  instancesCount: number;
}

export interface EquipmentListColumn extends SelectableColumn {
  className?: string;
  render: (equipment: Equipment, ctx: EquipmentListCellContext) => ReactNode;
}

/** First image of the equipment by display order */
function primaryImageUrl(equipment: Equipment) {
  const [first] = [...(equipment.images ?? [])].sort(
    (a, b) => (a.display_order ?? 0) - (b.display_order ?? 0),
  );
  return first?.image_url ?? null;
}

function text(value: string | null | undefined) {
  return value || "-";
}

function number(value: number | null | undefined, unit?: string) {
  if (value === null || value === undefined) return "-";
  const formatted = value.toLocaleString("de-CH");
  return unit ? `${formatted} ${unit}` : formatted;
}

function currency(value: number | null | undefined) {
  if (value === null || value === undefined) return "-";
  return formatCurrency(value);
}

/** All columns the equipment list can show, in display order. */
export const EQUIPMENT_LIST_COLUMNS: EquipmentListColumn[] = [
  {
    key: "photo",
    label: "Photo",
    className: "w-px",
    render: (e) => {
      const url = primaryImageUrl(e);
      if (!url) return null;
      // Thumbnail size grows with the viewport; originals are loaded lazily
      return (
        <img
          src={url}
          alt=""
          loading="lazy"
          decoding="async"
          className="h-10 w-10 max-w-none rounded-md border object-cover sm:h-12 sm:w-12 xl:h-16 xl:w-16"
          data-testid={`equipment-row-photo-${e.id}`}
        />
      );
    },
  },
  {
    key: "number",
    label: "Number",
    defaultVisible: true,
    className: "font-mono text-sm",
    render: (e) => e.number,
  },
  {
    key: "name",
    label: "Name",
    required: true,
    defaultVisible: true,
    className: "font-medium",
    render: (e) => (
      <span data-testid={`equipment-row-name-${e.id}`}>{e.name}</span>
    ),
  },
  {
    key: "short_name",
    label: "Short Name",
    render: (e) => text(e.short_name),
  },
  {
    key: "manufacturer",
    label: "Manufacturer",
    defaultVisible: true,
    render: (e) => text(e.manufacturer),
  },
  {
    key: "tags",
    label: "Tags",
    defaultVisible: true,
    render: (e) => (
      <div className="flex flex-wrap gap-1">
        {e.tags?.slice(0, 3).map((tag) => (
          <Badge key={tag.id} variant="outline" className="text-xs">
            {tag.name}
          </Badge>
        ))}
        {e.tags && e.tags.length > 3 && (
          <Badge variant="outline" className="text-xs">
            +{e.tags.length - 3}
          </Badge>
        )}
      </div>
    ),
  },
  {
    key: "stock",
    label: "Stock/Instances",
    defaultVisible: true,
    render: (e, ctx) =>
      e.is_serialized
        ? `${ctx.instancesCount} instances`
        : `${e.stock_quantity} in stock`,
  },
  {
    key: "location_in_warehouse",
    label: "Warehouse Location",
    render: (e) => text(e.location_in_warehouse),
  },
  {
    key: "rental_price",
    label: "Rental Price",
    className: "text-right tabular-nums",
    render: (e) => currency(e.rental_price),
  },
  {
    key: "new_price",
    label: "New Price",
    className: "text-right tabular-nums",
    render: (e) => currency(e.new_price),
  },
  {
    key: "weight",
    label: "Weight",
    className: "text-right tabular-nums",
    render: (e) => number(e.weight, "kg"),
  },
  {
    key: "dimensions",
    label: "Dimensions (L×W×H)",
    render: (e) =>
      e.length === null && e.width === null && e.height === null
        ? "-"
        : [e.length, e.width, e.height].map((v) => v ?? "?").join(" × "),
  },
  {
    key: "power",
    label: "Power",
    className: "text-right tabular-nums",
    render: (e) => number(e.power, "W"),
  },
  {
    key: "current_draw",
    label: "Current Draw",
    className: "text-right tabular-nums",
    render: (e) => number(e.current_draw, "A"),
  },
  {
    key: "country_of_origin",
    label: "Country of Origin",
    render: (e) => text(e.country_of_origin),
  },
  {
    key: "customs_tariff_number",
    label: "Customs Tariff Number",
    className: "font-mono text-sm",
    render: (e) => text(e.customs_tariff_number),
  },
  {
    key: "carnet_ata_value",
    label: "Carnet ATA Value",
    className: "text-right tabular-nums",
    render: (e) => currency(e.carnet_ata_value),
  },
  {
    key: "updated_at",
    label: "Last Changed",
    className: "tabular-nums whitespace-nowrap",
    render: (e) => text(formatDate(e.updated_at)),
  },
  {
    key: "notes",
    label: "Notes",
    className: "max-w-xs truncate",
    render: (e) => text(e.notes),
  },
];
